import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { cardAttributes } from '@sorare/core/src/lib/glossary';
import { withFragments } from '@sorare/core/src/lib/gql';

import { RuleHelperFnReturnType } from '../types';
import { getSerialNumberRule_so5Rules } from './__generated__/getSerialNumberRule.graphql';

const operatorToDisplay: Record<string, string> = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'gte',
};

export const getSerialNumberRule = withFragments(
  (
    rules: getSerialNumberRule_so5Rules,
    error: string
  ): RuleHelperFnReturnType => {
    const rule = rules.serialNumber;
    if (!rule) {
      return [];
    }

    const [[, operator, number]] = [
      ...rule.matchAll(/^(=|!=|>|>=|<|<=) ([1-9][0-9]*)$/g),
    ];

    if (!operator || !number || !(operator in operatorToDisplay)) {
      return null;
    }

    return {
      id: 'serialNumber',
      label: <FormattedMessage {...cardAttributes.serialNumber} />,
      description: (
        <FormattedMessage
          id="Rules.serialNumber.description"
          defaultMessage="{display, select,
            eq {Equal to}
            neq {Not equal to}
            gt {Greater than}
            gte {Greater than or equal to}
            lt {Less than}
            lte {Less than or equal to}
            other {}
          } {number}"
          values={{
            display: operatorToDisplay[operator],
            number,
          }}
        />
      ),
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M14 2C14 0.90625 13.0938 0 12 0H4C2.875 0 2 0.90625 2 2V14C2 15.125 2.875 16 4 16H12C13.0938 16 14 15.125 14 14V2Z"
            fill="url(#paint0_linear_371_1172)"
          />
          <path
            d="M6.93213 7.2207H6.03564L6.2334 6.48242H7.12988L7.60889 4.71143H8.4834L8.00439 6.48242H8.73389L9.19971 4.71143H10.0742L9.6084 6.48242H10.4917L10.2939 7.2207H9.41064L9.05469 8.50391H9.95117L9.75342 9.24219H8.85693L8.38672 11H7.51221L7.98242 9.24219H7.26172L6.78271 11H5.9082L6.38721 9.24219H5.49512L5.69287 8.50391H6.58496L6.93213 7.2207ZM8.18018 8.50391L8.53613 7.2207H7.80664L7.46387 8.50391H8.18018Z"
            fill="#101010"
          />
          <defs>
            <linearGradient
              id="paint0_linear_371_1172"
              x1="12.2426"
              y1="13.6569"
              x2="1.38148"
              y2="5.51099"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#D1DCDF" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
          </defs>
        </svg>
      ),
      error,
    };
  },
  {
    so5Rules: gql`
      fragment getSerialNumberRule_so5Rules on So5Rules {
        id
        serialNumber
      }
    ` as TypedDocumentNode<getSerialNumberRule_so5Rules>,
  }
);
